







import { useState } from "react";
import { motion } from "framer-motion";
import { createBlog } from "../api/apiServices";
import { useNavigate } from "react-router-dom";
import { FileText, Image as ImageIcon, Type } from "lucide-react";

export default function CreateBlog() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await createBlog({ title, content, image });
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8"
    >
      {/* Header */}
      <div className="max-w-3xl mx-auto text-center mb-10">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-4xl font-extrabold text-gray-800"
        >
          ✍️ Create a New Blog Post
        </motion.h2>
        <p className="text-gray-600 mt-2">
          Share your thoughts, ideas, and news with your community.
        </p>
      </div>

      {/* Form */}
      <motion.form
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-lg p-8 space-y-6 max-w-6xl mx-auto border border-gray-100"
      >
        {error && <p className="text-red-600">{error}</p>}

        {/* Title */}
        <div>
          <label className="block mb-1 font-medium text-gray-700 flex items-center gap-2">
            <Type size={18} /> Blog Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. The Future of Fundraising"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition"
          />
        </div>

        {/* Content */}
        <div>
          <label className="block mb-1 font-medium text-gray-700 flex items-center gap-2">
            <FileText size={18} /> Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your blog post here..."
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition"
            rows={8}
          />
        </div>

        {/* Image */}
        <div>
          <label className="block mb-1 font-medium text-gray-700 flex items-center gap-2">
            <ImageIcon size={18} /> Featured Image (URL)
          </label>
          <input
            value={image}
            onChange={(e) => {
              setImage(e.target.value);
              setPreview(e.target.value);
            }}
            placeholder="Paste an image URL here"
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition"
          />

          {/* Preview */}
          {preview && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4"
            >
              <img
                src={preview}
                alt="Preview"
                className="rounded-lg shadow-md max-h-64 mx-auto"
              />
            </motion.div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            className="px-5 py-2.5 border rounded-lg text-gray-600 hover:bg-gray-100 transition"
            onClick={() => navigate("/dashboard")}
          >
            Cancel
          </button>
          <button
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg shadow transition"
            disabled={loading}
          >
            {loading ? "Saving..." : "Publish Blog"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
